/*
 * This file is part of glowingblue/orell-facade.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';

const gaPosMap = {
	slider: 'Homepage_Slider',
	first_block: 'Homepage_Fenster1',
	second_block: 'Homepage_Fenster2',
	third_block: 'Homepage_Fenster3',
	fourth_block: 'Homepage_Fenster4',
	fifth_block: 'Homepage_Fenster5',
	sixth_block: 'Homepage_Fenster6',
	reading_round: 'Homepage_Leserunde',
	sponsor_above_reviews: 'Homepage_SponsorAboveReviews',
	sponsor_below_reviews: 'Homepage_SponsorBelowReviews',
	sponsor_between_community_events: 'Homepage_SponsorBetweenCommunityEvents',
};

export default class BannerFromState {
	constructor(banner) {
		if (!banner) {
			banner = app.store.createRecord('banners');
		}

		this.loading = false;

		this.banner = banner;
		this.bannerProps = { ...banner.props() } || {};

		this.expandedGroup = 'setup';
	}

	isExpanded(groupKey) {
		return this.expandedGroup === groupKey;
	}

	expand(groupKey) {
		this.expandedGroup = groupKey;

		m.redraw();
	}

	ensurePathExists(groupKey, name, device = null) {
		if (!this.bannerProps[groupKey]) {
			this.bannerProps[groupKey] = {};
		}

		if (device && !this.bannerProps[groupKey][name]) {
			this.bannerProps[groupKey][name] = {};
		}

		// Sometimes the default values of `{}` gets saved as `[]` in the database
		// because PHP thinks objects are arrays. This is a workaround for that.
		if (Array.isArray(this.bannerProps[groupKey])) {
			this.bannerProps[groupKey] = {};
		}
		if (device && Array.isArray(this.bannerProps[groupKey][name])) {
			this.bannerProps[groupKey][name] = {};
		}
	}

	get(groupKey, name, device = null) {
		this.ensurePathExists(groupKey, name, device);

		if (device === null) {
			return this.bannerProps[groupKey][name];
		} else {
			return this.bannerProps[groupKey][name][device];
		}
	}

	set(groupKey, name, device, value) {
		this.ensurePathExists(groupKey, name, device);

		if (value === undefined || value === null) {
			if (device) {
				delete this.bannerProps[groupKey][name][device];
			} else {
				delete this.bannerProps[groupKey][name];
			}

			return;
		}

		if (device) {
			this.bannerProps[groupKey][name][device] = value;
		} else {
			this.bannerProps[groupKey][name] = value;
		}

		// Handle GA Position
		if (groupKey === 'setup' && name === 'position') {
			this.ensurePathExists('ga', 'position');
			this.bannerProps.ga.position = gaPosMap[value];
		}

		// Update the banner model with the new props
		this.banner.pushAttributes({ props: this.bannerProps });
	}

	save() {
		this.loading = true;
		m.redraw();

		this.normalize();

		return this.banner.save({ props: this.bannerProps }).then(() => {
			this.loading = false;
			m.redraw();
		});
	}

	normalize() {
		// The dates (start and end) are stored as Date objects and should be strings before saving.
		['start_date', 'end_date'].forEach((key) => {
			if (this.bannerProps.setup[key]) {
				this.bannerProps.setup[key] = dayjs(this.bannerProps.setup[key]).format('YYYY-MM-DD HH:mm Z');
			}
		});
	}
}
